/** A volume is typed as a plain whole number, without a sign, a separator or a fraction. */
const WHOLE_NUMBER_PATTERN = /^\d+$/;

/**
 * Validates a single distributed volume. Every share has to be a whole number of steps.
 */
export const distributionVolumeValidator = (t, step) => (input) => {
    let err = null;
    const trimmedInput = String(input ?? '').trim();
    const parsedInput = Number.parseInt(trimmedInput, 10);
    if (trimmedInput.length === 0) {
        err = t('auctions.distribute-volume.validation.volume-empty');
    } else if (!WHOLE_NUMBER_PATTERN.test(trimmedInput)) {
        err = t('auctions.distribute-volume.validation.volume-invalid-integer');
    } else if (parsedInput <= 0) {
        err = t('auctions.distribute-volume.validation.volume-too-low');
    } else if (parsedInput % step !== 0) {
        err = t('auctions.distribute-volume.validation.volume-not-multiple', { step });
    }
    return err;
};

/**
 * Validates a single distributed auctioneer volume. The step rule applies to an auctioneer volume
 * exactly as it does to an auction volume, but zero is allowed: an auctioneer may legitimately
 * receive nothing from a particular auction.
 */
export const distributionAuctioneerVolumeValidator = (t, step) => (input) => {
    let err = null;
    const trimmedInput = String(input ?? '').trim();
    const parsedInput = Number.parseInt(trimmedInput, 10);
    if (trimmedInput.length === 0) {
        err = t('auctions.distribute-volume.validation.auctioneer-volume-empty');
    } else if (!WHOLE_NUMBER_PATTERN.test(trimmedInput)) {
        err = t('auctions.distribute-volume.validation.volume-invalid-integer');
    } else if (parsedInput % step !== 0) {
        err = t('auctions.distribute-volume.validation.volume-not-multiple', { step });
    }
    return err;
};

/**
 * Reports the auctions whose auctioneer volumes do not add up to the volume assigned to the
 * auction. The auctioneer lines of an auction always have to sum to its quantity, so a mismatch
 * blocks the distribution rather than merely warning about it.
 */
export const distributionAuctioneerSumError = (t) => (auctions) => {
    const mismatching = auctions.filter(({ volume, auctioneerTotal }) => (
        // A volume that cannot be read at all is already reported by the field itself, so it is
        // not repeated here as a sum mismatch.
        Number.isFinite(volume) && Number.isFinite(auctioneerTotal) && volume !== auctioneerTotal
    ));
    if (!mismatching.length) {
        return null;
    }
    return t('auctions.distribute-volume.validation.auctioneer-sum-mismatch', {
        auctions: mismatching.map(({ name }) => name).join(', '),
    });
};

/**
 * Reports that the distributed total does not match the volume of the cancelled auction. The whole
 * cancelled volume has to be placed, so a difference blocks the distribution rather than merely
 * warning about it.
 */
export const distributionTotalError = (t, cancelledQuantity) => (distributions) => {
    if (!distributions.length) {
        return null;
    }
    // A volume that cannot be read at all is already reported by the field itself, so the total is
    // left alone until every field holds a number.
    if (distributions.some(({ volume }) => !Number.isFinite(volume))) {
        return null;
    }
    const distributedTotal = distributions.reduce((sum, { volume }) => sum + volume, 0);
    if (distributedTotal === cancelledQuantity) {
        return null;
    }
    return t('auctions.distribute-volume.validation.total-mismatch', {
        distributed: distributedTotal,
        quantity: cancelledQuantity,
    });
};

/**
 * Reports the auctioneers whose volume is not distributed in full. The volume of an auctioneer has
 * to be redistributed to the very same auctioneer, so the volume it is allocated across all the
 * selected auctions has to add up to the volume it held on the cancelled auction.
 *
 * Each offending line is named by its auctioneer and its submarket code, because one auctioneer may
 * hold volume in more than one submarket and only the offending submarket has to be corrected.
 */
export const distributionAuctioneerAllocationError = (t) => (lines) => {
    const mismatching = lines.filter(({ complete, allocated, cancelled }) => (
        // An unreadable auctioneer volume is already reported by the field itself, so the line is
        // not accused of a mismatch while it is still being typed.
        complete && allocated !== cancelled
    ));
    if (!mismatching.length) {
        return null;
    }
    return t('auctions.distribute-volume.validation.auctioneer-allocation-mismatch', {
        lines: mismatching
            .map(({ name, memberId, submarketCode, allocated, cancelled }) => `${name} (${memberId}) / ${submarketCode}: ${allocated}/${cancelled}`)
            .join(', '),
    });
};
